package dairy.mynotes.addnotes

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import dairy.mynotes.navigation.Navigator
import dairy.mynotes.reusablecomponents.AppTopBar
import dairy.mynotes.reusablecomponents.BackIcon
import dairy.mynotes.reusablecomponents.ButtonComponent
import dairy.mynotes.reusablecomponents.CategoryDropdown
import dairy.mynotes.reusablecomponents.CircularProgressBarComponent
import dairy.mynotes.reusablecomponents.Label
import dairy.mynotes.reusablecomponents.TextInputComponent
import dairy.mynotes.ui.theme.AppColors

@Composable
fun AddNotesScreen(
    navigator: Navigator? = null,
    viewModel: AddNotesViewModel = viewModel { AddNotesViewModel() }
) {
    val uiState by viewModel.uiState.collectAsState()

    // Handle navigation after successful save - pop back to NotesList
    LaunchedEffect(uiState.isSaved) {
        if (uiState.isSaved) {
            navigator?.navigateBack()
        }
    }

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            AppTopBar(title = "Add Note", navigator = navigator, showBack = true)
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(horizontal = 20.dp)
                .padding(top = 20.dp)
                .verticalScroll(rememberScrollState())
        ) {
            // Category Dropdown — always shown; empty state handled inside
            Text(
                text = "Select from existing categories",
                fontSize = 15.sp,
                fontWeight = FontWeight.Medium,
                color = AppColors.TextSecondary,
                modifier = Modifier.padding(bottom = 8.dp)
            )

            if (uiState.availableCategories.isEmpty()) {
                Text(
                    text = "No categories yet. Type one below.",
                    fontSize = 13.sp,
                    color = AppColors.TextSecondary,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
            } else {
                CategoryDropdown(
                    categories = uiState.availableCategories,
                    selectedCategory = uiState.category,
                    onCategorySelected = viewModel::onCategoryChange,
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(modifier = Modifier.height(16.dp))
            }

            // Category Input (user can type or modify selected category)
            TextInputComponent(
                label = "Category",
                value = uiState.category,
                onValueChange = viewModel::onCategoryChange,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(16.dp))

            // Description Input (Multi-line)
            TextInputComponent(
                label = "Description",
                value = uiState.description,
                onValueChange = viewModel::onDescriptionChange,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(160.dp)
            )

            Spacer(modifier = Modifier.height(16.dp))

            // Error Message
            uiState.error?.let { errorMessage ->
                Text(
                    text = errorMessage,
                    color = AppColors.Error,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(bottom = 12.dp)
                )
            }

            // Loading Indicator
            if (uiState.isLoading) {
                CircularProgressBarComponent(modifier = Modifier.align(Alignment.CenterHorizontally))
                Spacer(modifier = Modifier.height(12.dp))
            }

            // Action Buttons
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Cancel Button
                ButtonComponent(
                    text = "Cancel",
                    modifier = Modifier.weight(1f).height(52.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AppColors.SurfaceVariant,
                        contentColor = AppColors.TextPrimary
                    ),
                    textColor = AppColors.TextPrimary,
                    onClick = { navigator?.navigateBack() },
                    enabled = !uiState.isLoading
                )

                // Save Button
                ButtonComponent(
                    text = "Save",
                    modifier = Modifier.weight(1f).height(52.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AppColors.Primary,
                        contentColor = AppColors.OnPrimary
                    ),
                    onClick = viewModel::saveNote,
                    enabled = !uiState.isLoading
                )
            }
            // Extra bottom padding so buttons clear the keyboard
            Spacer(modifier = Modifier.height(24.dp))
        }
    }
}
